import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';
import { ApplicationState } from '../../reducers';
import { useAppDispatch } from '../../store/ReduxStore';
import {
  fetchAdminUsers,
  fetchAvailableAccounts,
  updateUserAccountAssignments,
} from '../../actions/multiAccountUser';
import { fetchAccessibleAccounts } from '../../actions/user';
import { hasPermission } from '../../utils/Permissions';
import PageHookForm, { CustomHookFormActions } from '../../components/global/page/PageHookForm';
import AdminAccountManagementForm from './AdminAccountManagementForm';

const { Title } = Typography;

const AdminAccountManagementContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { adminUsers, fetchingAdminUsers, availableAccounts, user } = useSelector(
    (state: ApplicationState) => ({
      adminUsers: state.multiAccountUser.adminUsers,
      fetchingAdminUsers: state.multiAccountUser.fetchingAdminUsers,
      availableAccounts: state.multiAccountUser.availableAccounts,
      user: state.user.user,
    })
  );

  const canManageSuperAdmin = useMemo(() => hasPermission(user, 'can_manage_super_admin'), [user]);

  // Selected user ID is NOT part of form values (doesn't trigger dirty state)
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);

  useEffect(() => {
    if (canManageSuperAdmin) {
      dispatch(fetchAdminUsers());
      dispatch(fetchAvailableAccounts());
    }
  }, [dispatch, canManageSuperAdmin]);

  const initialValues = useMemo(
    () => ({
      targetKeys: [] as string[],
    }),
    []
  );

  const handleSubmit = async (values: FieldValues, actions: CustomHookFormActions) => {
    const { setSubmitPending, setSubmitError, setSubmitSuccess, reset } = actions;

    if (!selectedUserId) {
      setSubmitError(t('adminAccountManagement:selectUserFirst'));
      return;
    }

    setSubmitPending();

    try {
      await dispatch(
        updateUserAccountAssignments(selectedUserId, {
          account_ids: values.targetKeys.map((k: string) => Number(k)),
        })
      );

      // If the modified user is the current logged-in user, refresh their accessible accounts
      if (selectedUserId === user?.id) {
        await dispatch(fetchAccessibleAccounts());
      }

      // Reset form to mark as clean (updateUserAccountAssignments already triggers fetchAdminUsers)
      reset(values);

      // Use setSubmitSuccess for proper savebar feedback (green indicator)
      setSubmitSuccess();
    } catch (error: any) {
      setSubmitError(error.response?.data?.message || t('adminAccountManagement:updateError'));
    }
  };

  if (!canManageSuperAdmin) {
    return (
      <div className="admin-account-management admin-account-management--unauthorized">
        <Title level={3}>{t('adminAccountManagement:unauthorizedTitle')}</Title>
        <p>{t('adminAccountManagement:unauthorizedMessage')}</p>
      </div>
    );
  }

  if (fetchingAdminUsers) {
    return (
      <div className="flex justify-center items-center flex-1">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageHookForm initialValues={initialValues} onSubmit={handleSubmit} contentNoSpacing>
      {formProps => (
        <AdminAccountManagementForm
          formProps={formProps}
          adminUsers={adminUsers}
          availableAccounts={availableAccounts}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
        />
      )}
    </PageHookForm>
  );
};

export default AdminAccountManagementContainer;
